import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, Edit2, Trash2, Search, Dumbbell, Target, ChevronLeft } from "lucide-react";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { DAY_TYPES } from "../../lib/training";

export default function AdminTemplates() {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sessionTpls, setSessionTpls] = useState([]);
  const [testTpls, setTestTpls] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/session-templates"),
        api.get("/admin/test-templates"),
      ]);
      setSessionTpls(a.data || []);
      setTestTpls(b.data || []);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (name) => !q || name.toLowerCase().includes(q);
    return {
      sessions: (filter === "tests" ? [] : sessionTpls).filter((t) => matches(t.name)),
      tests:    (filter === "sessions" ? [] : testTpls).filter((t) => matches(t.name)),
    };
  }, [filter, search, sessionTpls, testTpls]);

  const renameSession = async (tpl) => {
    const name = window.prompt("Renombrar plantilla de entrenamiento:", tpl.name);
    if (!name || !name.trim() || name === tpl.name) return;
    try {
      await api.put(`/admin/session-templates/${tpl.id}`, { name: name.trim() });
      toast.success("Renombrada"); load();
    } catch (err) { toast.error(formatErr(err)); }
  };
  const deleteSession = async (tpl) => {
    if (!confirm(`¿Eliminar la plantilla "${tpl.name}"?`)) return;
    try { await api.delete(`/admin/session-templates/${tpl.id}`); toast.success("Eliminada"); load(); }
    catch (err) { toast.error(formatErr(err)); }
  };
  const renameTest = async (tpl) => {
    const name = window.prompt("Renombrar plantilla de test:", tpl.name);
    if (!name || !name.trim() || name === tpl.name) return;
    try {
      await api.put(`/admin/test-templates/${tpl.id}`, { name: name.trim() });
      toast.success("Renombrada"); load();
    } catch (err) { toast.error(formatErr(err)); }
  };
  const deleteTest = async (tpl) => {
    if (!confirm(`¿Eliminar la plantilla "${tpl.name}"?`)) return;
    try { await api.delete(`/admin/test-templates/${tpl.id}`); toast.success("Eliminada"); load(); }
    catch (err) { toast.error(formatErr(err)); }
  };

  const total = sessionTpls.length + testTpls.length;
  const shown = filtered.sessions.length + filtered.tests.length;

  return (
    <AppShell>
      <div className="qv-page-head" data-testid="admin-templates">
        <Link to="/app/admin" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, letterSpacing: "0.22em", color: "#737373", textTransform: "uppercase", fontWeight: 700, marginBottom: 14, textDecoration: "none" }}>
          <ChevronLeft size={14} /> Admin
        </Link>
        <div className="label">Biblioteca</div>
        <h1>Plantillas globales</h1>
        <p className="sub">Todas tus plantillas de entrenamientos y tests en un solo sitio. Reutilízalas con cualquier cliente.</p>
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "inline-flex", border: "1px solid rgba(255,255,255,0.08)" }}>
          {[
            ["all",      `Todas (${total})`],
            ["sessions", `Entrenos (${sessionTpls.length})`],
            ["tests",    `Tests (${testTpls.length})`],
          ].map(([v, lbl]) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              data-testid={`filter-${v}`}
              style={{
                background: filter === v ? "rgba(225,29,46,0.12)" : "transparent",
                color: filter === v ? "var(--qv-red)" : "#cfcfcf",
                border: "none",
                borderRight: "1px solid rgba(255,255,255,0.05)",
                padding: "8px 14px",
                fontSize: 11,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >{lbl}</button>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
          <Search size={13} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#737373" }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre..."
            data-testid="tpl-search"
            style={{ width: "100%", padding: "10px 12px 10px 32px", background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", fontSize: 13 }}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ color: "#737373" }}>Cargando...</div>
      ) : shown === 0 ? (
        <div className="qv-empty">
          {total === 0
            ? "Aún no has guardado ninguna plantilla. Crea una sesión o test y pulsa GUARDAR COMO PLANTILLA."
            : "No hay plantillas que coincidan con tu búsqueda."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          {/* Session templates */}
          {filtered.sessions.length > 0 && (
            <div>
              <div className="qv-overline" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <Dumbbell size={12} /> Entrenamientos · {filtered.sessions.length}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
                {filtered.sessions.map((tpl) => {
                  const dt = DAY_TYPES[tpl.day_type] || {};
                  const totalEx = (tpl.blocks || []).reduce((n, b) => n + (b.exercises || []).length, 0);
                  return (
                    <div key={tpl.id} className="qv-card-app" style={{ padding: 14 }} data-testid={`session-tpl-${tpl.id}`}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        {dt.color && <span style={{ width: 8, height: 8, background: dt.color }} />}
                        <div style={{ fontWeight: 700, fontSize: 14, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tpl.name}</div>
                      </div>
                      <div style={{ fontSize: 10, color: "#737373", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 10 }}>
                        {dt.label || tpl.day_type} · {(tpl.blocks || []).length} bloques · {totalEx} ejercicios
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => renameSession(tpl)} className="qv-btn-ghost" style={{ fontSize: 10 }} data-testid={`rename-session-${tpl.id}`}>
                          <Edit2 size={11} /> RENOMBRAR
                        </button>
                        <button onClick={() => deleteSession(tpl)} className="qv-btn-ghost" style={{ fontSize: 10, color: "var(--qv-red)", borderColor: "rgba(225,29,46,0.4)" }}>
                          <Trash2 size={11} /> ELIMINAR
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Test templates */}
          {filtered.tests.length > 0 && (
            <div>
              <div className="qv-overline" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 6, color: "#EAB308" }}>
                <Target size={12} /> Tests · {filtered.tests.length}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
                {filtered.tests.map((tpl) => (
                  <div key={tpl.id} className="qv-card-app" style={{ padding: 14, borderColor: "rgba(234,179,8,0.25)" }} data-testid={`test-tpl-${tpl.id}`}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <Target size={14} color="#EAB308" />
                      <div style={{ fontWeight: 700, fontSize: 14, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tpl.name}</div>
                    </div>
                    <div style={{ fontSize: 10, color: "#737373", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 10 }}>
                      {tpl.test_type}{tpl.unit ? ` · ${tpl.unit}` : ""}{tpl.description ? " · con instrucciones" : ""}{tpl.tutorial_url ? " · con vídeo" : ""}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => renameTest(tpl)} className="qv-btn-ghost" style={{ fontSize: 10 }} data-testid={`rename-test-${tpl.id}`}>
                        <Edit2 size={11} /> RENOMBRAR
                      </button>
                      <button onClick={() => deleteTest(tpl)} className="qv-btn-ghost" style={{ fontSize: 10, color: "var(--qv-red)", borderColor: "rgba(225,29,46,0.4)" }}>
                        <Trash2 size={11} /> ELIMINAR
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
